import { z } from "zod";
import { managerColorSchema } from "./colors";
import { csvListSchema, listQuerySchema } from "./pagination";

/** Decision Log row 14, ours: the card layout depends on a bound, so 80 characters. */
export const OPPORTUNITY_NAME_MAX = 80;

export const opportunityStatusSchema = z.enum(["open", "won", "lost"]);
export type OpportunityStatus = z.infer<typeof opportunityStatusSchema>;

export const stageSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  displayOrder: z.number().int(),
  closedType: opportunityStatusSchema,
  active: z.boolean(),
});
export type Stage = z.infer<typeof stageSchema>;

/**
 * An opportunity as the client reads it.
 *
 * `quarter`, `year` and `status` are **derived server-side** and arrive as data. Nothing
 * in the browser may compute them — that is what keeps the list, the board and the export
 * from disagreeing.
 */
export const opportunitySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /**
   * The URL identity — client name and opportunity name, derived and stored server-side.
   * Regenerated when either name changes; the outgoing value redirects forever.
   */
  slug: z.string(),
  /** Decimal string, never a float: money must not lose precision in transit. */
  nip: z.string(),
  expectedCloseDate: z.string(),
  actualCloseDate: z.string().nullable(),

  clientId: z.string().uuid(),
  clientName: z.string(),
  /** Their URL identities, so a detail can link to them without a second request. */
  clientSlug: z.string(),
  ownerId: z.string().uuid(),
  ownerName: z.string(),
  ownerSlug: z.string(),
  /** The owner's palette key — the detail header is the board's colour legend. */
  ownerColor: managerColorSchema.nullable(),
  stageId: z.string().uuid(),
  stageName: z.string(),
  /** The stage's palette colour, joined for the list's chip. Null when unassigned. */
  stageColor: managerColorSchema.nullable(),

  quarter: z.number().int(),
  year: z.number().int(),
  status: opportunityStatusSchema,
  /**
   * How long it has sat in its stage, and whether that makes it stalled.
   *
   * Both come from `opportunity_derived` — the same view the board reads. The detail shows
   * "how long it has been there" from the server's derivation, never from a date difference
   * computed in the browser, which is the defect class the always-on rule names.
   */
  stageAgeDays: z.number().int(),
  stalled: z.boolean(),

  /** One free-text note, or nothing. Null is "no note" — there is no empty-string state. */
  notes: z.string().nullable(),
  /**
   * Why the deal was lost. Present only on a lost opportunity, and **required** once it is one
   * — the interface refuses to leave a lost record without it, and anything closed before this
   * shipped is prompted for at sign-in. Null on anything not lost.
   */
  lostReason: z.string().nullable(),

  stageEnteredAt: z.string(),
  deletedAt: z.string().nullable(),
  version: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type Opportunity = z.infer<typeof opportunitySchema>;

/** The four values a user supplies. Everything else is derived or set by an operation. */
export const createOpportunitySchema = z.object({
  clientId: z.string().uuid("Choose a client"),
  name: z.string().trim().min(1, "Enter a name").max(OPPORTUNITY_NAME_MAX, `Use at most ${OPPORTUNITY_NAME_MAX} characters`),
  nip: z
    .string()
    .trim()
    .regex(/^\d+(\.\d{1,2})?$/, "Enter an amount, up to two decimal places")
    .refine((value) => Number(value) > 0, "Must be greater than zero"),
  expectedCloseDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Enter a valid date"),
  /** Head of Sales only; ignored for a Sales Manager, who always owns what they create. */
  ownerId: z.string().uuid().optional(),
});
export type CreateOpportunityInput = z.infer<typeof createOpportunitySchema>;

/**
 * An edit, and the version it was read at.
 *
 * Every field is optional because the detail now saves **one field per request**: a click
 * on a value becomes an input, and Enter or blur sends exactly what changed with the latest
 * version. Cancelling sends nothing at all.
 *
 * `notes` accepts `null` explicitly — clearing a note is a value, not an omission, and
 * `partial()` alone could not tell "leave it alone" from "remove it".
 */
/**
 * The shortest reason worth recording.
 *
 * Three characters, trimmed: enough to refuse an empty box or a stray keystroke, short enough
 * that "PoC" and "£££" are accepted answers — the point is that somebody thought about it, not
 * that they wrote prose. Shared by the closure dialog, the detail field and the sign-in sweep,
 * so no path can be laxer than another.
 */
export const LOST_REASON_MIN = 3;

export const lostReasonSchema = z
  .string()
  .trim()
  .min(LOST_REASON_MIN, `Say why in at least ${LOST_REASON_MIN} characters`);

export const updateOpportunitySchema = createOpportunitySchema
  .omit({ ownerId: true })
  .partial()
  .extend({
    notes: z.string().nullable().optional(),
    /** Settable on its own once the record is lost — the detail's inline field uses this. */
    lostReason: lostReasonSchema.optional(),
    version: z.number().int(),
  });
export type UpdateOpportunityInput = z.infer<typeof updateOpportunitySchema>;

/**
 * Reassignment. Head of Sales only, and **the stage timer is not reset** — a deal that has
 * sat for fifty days has still sat for fifty days after it changes hands.
 */
export const reassignOwnerSchema = z.object({
  ownerId: z.string().uuid("Choose a sales manager"),
  version: z.number().int(),
});
export type ReassignOwnerInput = z.infer<typeof reassignOwnerSchema>;

/**
 * The stalled filter — one flag, and only ever "on".
 *
 * Added by the analytics increment as a **landing site for an analytics click**, not as a
 * knob: the Stalled tile has to open the opportunities behind it, and "a number a user
 * cannot open is a number they cannot act on". `sales-board.md` deferred it as one `where`
 * clause and predicted exactly this arrival.
 *
 * There is no `stalled=false`: "healthy" is not a question anyone asks of the pipeline, and
 * an off value that means something would need a control the filter panel deliberately does
 * not give it.
 */
export const stalledFilterSchema = z.enum(["true"]).optional();

/**
 * The four multi-select filters, declared once and worn by list, board and analytics.
 *
 * **Every select filter accepts a set** (`stage=a,b,c`): what a view is filtered to must
 * always be expressible in the filter panel, and one filter left single-valued would
 * recreate exactly the mismatch this reverses. See specs/filtering-multiselect.md.
 *
 * `status` is deliberately **absent**, not optional. It was only ever an analytics landing
 * param, and it duplicated what the phase filter already carries — "open" is the set of
 * active open phases, and won/lost are phases too. Two vocabularies for one fact is the
 * mismatch itself, so a request still carrying `status=` is a validation error rather than
 * a parameter quietly ignored: every query schema below is `.strict()` for exactly that
 * reason. A removed parameter that returns 200 is a client that keeps sending it.
 */
const uuidFilterList = () => csvListSchema(z.string().uuid());
const quarterFilterList = () => csvListSchema(z.coerce.number().int().min(1).max(4));

export const opportunityFilterParams = {
  client: uuidFilterList(),
  owner: uuidFilterList(),
  stage: uuidFilterList(),
  quarter: quarterFilterList(),
} as const;

export const opportunitiesQuerySchema = listQuerySchema.extend({
  ...opportunityFilterParams,
  year: z.coerce.number().int().min(2000).max(2100).optional(),
  stalled: stalledFilterSchema,
  /** The deleted-items view opts in explicitly; every other caller never sees them. */
  includeDeleted: z.enum(["true"]).optional(),
}).strict();

export const OPPORTUNITY_SORT_FIELDS = [
  "name",
  "clientName",
  "nip",
  "expectedCloseDate",
  "stage",
  "ownerName",
  "createdAt",
] as const;

// ---------------------------------------------------------------------------
// Board — the stage×quarter view. See specs/sales-board.md
// ---------------------------------------------------------------------------

/**
 * Move between open stages. The optional date makes a diagonal drag — stage and
 * quarter in one gesture — a single transaction and a single version check.
 */
export const moveStageSchema = z.object({
  stageId: z.string().uuid(),
  expectedCloseDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  version: z.number().int(),
});
export type MoveStageInput = z.infer<typeof moveStageSchema>;

/** Closing requires an Actual Close Date — the book makes it mandatory when closed. */
export const closeOpportunitySchema = z.object({
  stageId: z.string().uuid(),
  actualCloseDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  /**
   * Required when the target stage is a **lost** one, refused otherwise — the API decides which
   * from the stage, not from what the client claims. Carried on the closure itself so a lost
   * deal cannot exist without a reason even for the moment between two requests.
   */
  lostReason: lostReasonSchema.optional(),
  version: z.number().int(),
});
export type CloseOpportunityInput = z.infer<typeof closeOpportunitySchema>;

export const reopenOpportunitySchema = z.object({
  stageId: z.string().uuid(),
  expectedCloseDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  version: z.number().int(),
});
export type ReopenOpportunityInput = z.infer<typeof reopenOpportunitySchema>;

/**
 * Lost opportunities of the reader's own that still have no reason.
 *
 * Scoped to the requester server-side: this drives a dialog nobody can dismiss, so it must
 * never be possible to be held hostage by somebody else's record. Ordered oldest-closed first,
 * because the one closed longest ago is the one nobody has explained.
 */
export const pendingLostReasonSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  clientName: z.string(),
  stageName: z.string(),
  actualCloseDate: z.string().nullable(),
  version: z.number().int(),
});
export type PendingLostReason = z.infer<typeof pendingLostReasonSchema>;

/**
 * A card. Deliberately the four facts the book puts on a card plus what the grid and
 * its permissions need — never a full opportunity, because the board renders hundreds.
 *
 * `quarter`, `year`, `status`, `stageAgeDays` and `stalled` are all **derived
 * server-side** and arrive as data. Nothing in the browser computes them, and in
 * particular nothing computes a day difference.
 */
export const boardCardSchema = z.object({
  id: z.string().uuid(),
  clientName: z.string(),
  name: z.string(),
  /** Where clicking the card goes — the card's URL identity, derived server-side. */
  slug: z.string(),
  nip: z.string(),
  stageId: z.string().uuid(),
  quarter: z.number().int(),
  year: z.number().int(),
  status: opportunityStatusSchema,
  stageAgeDays: z.number().int(),
  stalled: z.boolean(),
  ownerId: z.string().uuid(),
  ownerName: z.string(),
  /**
   * The owner's palette key, so the card can carry their colour. Null tints nothing —
   * an unassigned manager's cards keep the plain surface.
   */
  ownerColor: managerColorSchema.nullable(),
  expectedCloseDate: z.string(),
  /**
   * Present on a closed card. Carried so that correcting Won → Lost can offer the date
   * already recorded instead of defaulting to today and silently moving the outcome by
   * however long the correction took.
   */
  actualCloseDate: z.string().nullable(),
  version: z.number().int(),
});
export type BoardCard = z.infer<typeof boardCardSchema>;

/** Computed in SQL, never summed in the browser — see the spec's reasoning. */
export const boardCellAggregateSchema = z.object({
  stageId: z.string().uuid(),
  quarter: z.number().int(),
  count: z.number().int(),
  totalNip: z.string(),
});
export type BoardCellAggregate = z.infer<typeof boardCellAggregateSchema>;

/**
 * The whole board's count and value, under the same filters.
 *
 * Here rather than summed from `cells` in the browser for the same reason the cells are:
 * a client-side sum is how a header figure and an export start to disagree, and money added
 * up as JavaScript numbers is not money.
 */
export const boardTotalsSchema = z.object({
  count: z.number().int(),
  totalNip: z.string(),
});
export type BoardTotals = z.infer<typeof boardTotalsSchema>;

/**
 * One stage's count and value, under the same filters — the row header's own figure.
 *
 * Computed in SQL, grouped by stage alone, for the same reason `cells` and `totals` are:
 * a client-side sum over `cells` is how a row header and an export begin to disagree, and
 * money added as JavaScript numbers is not money. A collapsed row keeps this figure — that
 * is the point of showing it here rather than only inside the cells it is hiding.
 */
export const boardRowAggregateSchema = z.object({
  stageId: z.string().uuid(),
  count: z.number().int(),
  totalNip: z.string(),
});
export type BoardRowAggregate = z.infer<typeof boardRowAggregateSchema>;

export const boardResponseSchema = z.object({
  year: z.number().int(),
  /** Derived server-side from the editable-year window, like every other derived value. */
  editable: z.boolean(),
  editableYears: z.array(z.number().int()),
  stages: z.array(stageSchema),
  cells: z.array(boardCellAggregateSchema),
  totals: boardTotalsSchema,
  rows: z.array(boardRowAggregateSchema),
  items: z.array(boardCardSchema),
});
export type BoardResponse = z.infer<typeof boardResponseSchema>;

export const boardQuerySchema = z.object({
  year: z.coerce.number().int().min(2000).max(2100).optional(),
  ...opportunityFilterParams,
  /**
   * Same shape as the list's `q`, deliberately: one filter bar emits one search term, and
   * the two views run it through the same predicate. A term that narrowed the list but was
   * ignored by the board was the filter-consistency defect this increment closes.
   */
  q: z.string().trim().min(1).optional(),
  /**
   * Stalled — the one filter an analytics tile clicks through with that no phase set can
   * express. It lives here, on the board's own schema, which analytics extends, so the
   * list, the board and analytics cannot disagree about what a filtered view contains. It
   * is the only filter with no control in the panel; it still gets a chip.
   *
   * `status` used to sit beside it and no longer exists anywhere — see
   * `opportunityFilterParams`.
   */
  stalled: stalledFilterSchema,
}).strict();
export type BoardQuery = z.infer<typeof boardQuerySchema>;

/**
 * The year window, computed server-side in `APP_TIMEZONE`.
 *
 * The list view has no board response to learn the window from, and "which year is it"
 * asked of the browser is the timezone defect class the derivation rule forbids — on
 * 31 December a browser outside Prague answers differently.
 */
export const yearsResponseSchema = z.object({
  currentYear: z.number().int(),
  /** Years that may be written. */
  editableYears: z.array(z.number().int()),
  /** Everything the selector offers — the editable window preceded by two archive years. */
  selectableYears: z.array(z.number().int()),
});
export type YearsResponse = z.infer<typeof yearsResponseSchema>;

/**
 * The quarter preview's only input. A schema rather than a hand-rolled check in the query
 * layer: validation belongs at the boundary, stated once, in the same place as every other
 * request's rules.
 */
export const closeDatePreviewQuerySchema = z.object({
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Give the date as yyyy-MM-dd")
    .refine((value) => !Number.isNaN(new Date(`${value}T12:00:00Z`).getTime()), "That is not a real date"),
});
export type CloseDatePreviewQuery = z.infer<typeof closeDatePreviewQuerySchema>;

/**
 * How many cards a cell shows before "+ N more". A constant, not a setting — the book's
 * configuration page is explicit that v1.0 exposes exactly one knob, and this is not it.
 */
export const BOARD_CELL_CARD_CAP = 7;

/** Row 19: validated at write time; the interface warns below 30 once a UI exists. */
export const STALLED_THRESHOLD_RANGE = { min: 7, max: 365, warnBelow: 30 } as const;
export const STALLED_THRESHOLD_KEY = "stalled_threshold_days" as const;
